import { all, call, put, takeEvery, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { Comment } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import {
  CreateCommentRequest,
  CreateCommentResponse,
  DeleteCommentRequest,
  DeleteCommentResponse,
  ViewCommentsRequest,
  ViewCommentsResponse
} from './comment.types';

import { CREATING_COMMENT, DELETING_COMMENT } from './comment.constants';

import { requestCreateComment, requestDeleteComment, requestViewComments } from './comment.actions';

import { getCreateCommentRequest, getDeleteCommentRequest, getViewCommentsRequest } from './comment.endpoints';

import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateCommentRequest({ payload }: SagaPayload<CreateCommentRequest, string | undefined>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_COMMENT));

    const [endpoint, options] = getCreateCommentRequest(requestData);

    const { data }: AxiosResponse<CreateCommentResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.commentExternalReference);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_COMMENT));
  }
}

export function* watchForCreateCommentRequest() {
  yield takeLatest(requestCreateComment.type, performCreateCommentRequest);
}

export function* performViewCommentsRequest({ payload }: SagaPayload<ViewCommentsRequest, Comment[]>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewCommentsRequest(requestData);

    const { data }: AxiosResponse<ViewCommentsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.comments);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewCommentsRequest() {
  yield takeEvery(requestViewComments.type, performViewCommentsRequest);
}

export function* performDeleteCommentRequest({ payload }: SagaPayload<DeleteCommentRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_COMMENT));

    const [endpoint, options] = getDeleteCommentRequest(requestData);

    const { data }: AxiosResponse<DeleteCommentResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_COMMENT));
  }
}

export function* watchForDeleteCommentRequest() {
  yield takeLatest(requestDeleteComment.type, performDeleteCommentRequest);
}

export default function* commentSaga() {
  yield all([watchForCreateCommentRequest(), watchForViewCommentsRequest(), watchForDeleteCommentRequest()]);
}
